'use client';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { bgPrimary } from '@osmosis/tailwind-ui/styles/background';
import { text0, textBlue } from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { Route } from '../hooks/useRouterPush';
import { getAuthPath } from '../lib/composer/url';
import type { TrialCtaArgs } from '../lib/composer/schemas/answersValueProp';

type Props = {
  copy?: TrialCtaArgs;
  isLoading?: boolean;
  onButtonClick?: React.MouseEventHandler<HTMLAnchorElement>;
  target?: string;
  containerTestId?: string;
  buttonTestId?: string;
  fineprintTestId?: string;
  classNames?: {
    container?: string;
    button?: string;
    fineprint?: string;
  };
};

/**
 * Shared free-trial CTA used by logged-out Answer-page surfaces. The visual
 * treatment and registration destination are code-owned so placements cannot
 * drift; Composer controls only the two pieces of copy.
 */
const TrialCTA = ({
  copy,
  isLoading,
  onButtonClick,
  target,
  containerTestId = 'trial-cta',
  buttonTestId = 'trial-cta-button',
  fineprintTestId = 'trial-cta-fineprint',
  classNames,
}: Props) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return (
    <div
      data-testid={containerTestId}
      className={cn(
        `flex w-fit flex-col items-center gap-2 p-3 ${bgPrimary}`,
        classNames?.container,
      )}
    >
      <Button
        as="a"
        color="white"
        rounded
        href={getAuthPath(Route.CREATE, pathname, searchParams)}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        isLoading={isLoading}
        onClick={onButtonClick}
        data-testid={buttonTestId}
        className={cn(`${textBlue} px-6`, classNames?.button)}
      >
        {copy?.buttonLabel ?? (
          <FormattedMessage
            id="answers.trialCta.button"
            defaultMessage="Try Osmosis Free"
          />
        )}
      </Button>

      <p
        data-testid={fineprintTestId}
        className={cn(`text-center text-sm ${text0}`, classNames?.fineprint)}
      >
        {copy?.fineprint ?? (
          <FormattedMessage
            id="answers.trialCta.fineprint"
            defaultMessage="No credit card required"
          />
        )}
      </p>
    </div>
  );
};

export default TrialCTA;
